import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Mic, Image as ImageIcon, MessageSquare } from "lucide-react";

const KINDS = [["all", "All"], ["voice", "Voice notes"], ["photo", "Photos"]];

export default function SearchDialog({ open, onClose, frigate, onOpenConversation }) {
  const [q, setQ] = useState("");
  const [sender, setSender] = useState("");
  const [kind, setKind] = useState("all");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (!q.trim() && !sender.trim() && kind === "all") { setResults([]); return; }
      setLoading(true);
      api.get("/island/search", { params: { q, sender, kind, frigate: frigate || "" } }).then((r) => setResults(r.data.results)).catch(() => setResults([])).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, sender, kind, frigate, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-2xl" data-testid="search-dialog">
        <DialogHeader><DialogTitle className="font-display">Search the Island</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keyword…" className="pl-9" data-testid="search-keyword-input" /></div>
          <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="Sender name" className="w-44" data-testid="search-sender-input" />
        </div>
        <div className="flex gap-2">
          {KINDS.map(([k, l]) => <button key={k} onClick={() => setKind(k)} data-testid={`search-kind-${k}`} className={`rounded-full px-3 py-1 text-xs font-600 ${kind === k ? "bg-[hsl(var(--water))] text-white" : "bg-secondary text-muted-foreground"}`}>{l}</button>)}
        </div>
        <div className="max-h-[50vh] space-y-1 overflow-y-auto" data-testid="search-results">
          {results.map((m) => (
            <button key={m.id} onClick={() => { onOpenConversation(m.conversation_id); onClose(); }} data-testid={`search-result-${m.id}`}
              className="flex w-full items-start gap-3 rounded-lg p-2.5 text-left hover:bg-secondary">
              <div className="mt-0.5 text-[hsl(var(--gold))]">{m.audio_path ? <Mic className="h-4 w-4" /> : m.image_path ? <ImageIcon className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{m.content || (m.audio_path ? `Voice note (${m.audio_seconds || "?"}s)` : "Photo")}</p>
                <p className="text-[0.65rem] text-muted-foreground">{m.sender_name} · {new Date(m.created_at).toLocaleString()}</p>
              </div>
              <Badge variant="outline" className="shrink-0 text-[0.6rem]">{m.conversation_name}</Badge>
            </button>
          ))}
          {!loading && results.length === 0 && (q || sender || kind !== "all") && <p className="p-4 text-center text-sm text-muted-foreground">No messages found.</p>}
          {!q && !sender && kind === "all" && <p className="p-4 text-center text-sm text-muted-foreground">Type a keyword or sender, or pick Voice notes / Photos.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
